import { useState, type FormEvent, useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { Navigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '../components/Icons';

// FIX: Removed duplicate global declaration for import.meta.env to prevent type conflicts.
// The correct type is now provided from `src/auth/http.ts`.
const isMock = import.meta.env.VITE_MOCK_API === 'true';

const testUsers = [
  { name: 'Admin (Controle Total)', email: 'admin@dev', pass: 'admin123' },
  { name: 'Infraestrutura (Segurança)', email: 'infra@dev', pass: 'user123' },
  { name: 'Gestor de Beneficiários', email: 'benef@dev', pass: 'user123' },
  { name: 'Gestor de Prestadores', email: 'prestador@dev', pass: 'user123' },
  { name: 'Financeiro (Consulta)', email: 'financeiro@dev', pass: 'user123' },
];

function Login() {
  const { session, login, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);
  
  const handleTestUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUser = testUsers.find(u => u.email === e.target.value);
    if (selectedUser) {
      setEmail(selectedUser.email);
      setPassword(selectedUser.pass);
    } else {
      setEmail('');
      setPassword('');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
    } catch (err) {
      setError((err as Error).message || 'Falha no login. Verifique suas credenciais.');
    }
  };
  
  if (session) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>Acessar SisOptimed</h2>
        <form onSubmit={handleSubmit} className="login-form">
           {isMock && (
            <div className="form-group">
              <label htmlFor="test-user-selector">Login Rápido (Dev)</label>
              <select
                id="test-user-selector"
                className="form-input"
                value={email}
                onChange={handleTestUserChange}
              >
                <option value="">Selecione um perfil...</option>
                {testUsers.map(user => (
                  <option key={user.email} value={user.email}>{user.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              ref={emailInputRef}
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <button 
                type="button" 
                className="password-toggle-btn"
                onClick={() => setShowPassword(prev => !prev)}
                aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                title={showPassword ? "Esconder senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? <span className="spinner"></span> : 'Entrar'}
          </button>
        </form>
        {error && <div className="login-error">{error}</div>}
        {!isMock && (
            <div className="login-help">
              <p>Usuários de teste (API):</p>
              <ul>
                <li><b>Admin:</b> admin@dev / admin123</li>
                <li><b>Infra:</b> infra@dev / user123</li>
                <li><b>Beneficiários:</b> benef@dev / user123</li>
                <li><b>Prestadores:</b> prestador@dev / user123</li>
                <li><b>Financeiro:</b> financeiro@dev / user123</li>
              </ul>
            </div>
        )}
      </div>
    </div>
  );
}

export default Login;